import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Upload, RotateCcw, ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { IMAGE_SLOTS, DEFAULT_IMAGES, TIMELINE } from "@/constants/site";
import { useImageOverrides, useTimeline, useRefreshSiteContent } from "@/context/ImagesContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fieldCls =
  "w-full bg-espresso border border-cream/15 rounded-sm px-3.5 py-2.5 text-base sm:text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold transition-colors";

const AdminImages = () => {
  const overrides = useImageOverrides();
  const timelineOverride = useTimeline();
  const refresh = useRefreshSiteContent();
  const [passcode, setPasscode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [current, setCurrent] = useState({});
  const [busy, setBusy] = useState({});
  const [milestones, setMilestones] = useState(null);
  const [savingTimeline, setSavingTimeline] = useState(false);
  const fileInputs = useRef({});

  useEffect(() => {
    setCurrent((c) => ({ ...overrides, ...c }));
  }, [overrides]);

  useEffect(() => {
    if (milestones === null && timelineOverride !== undefined) {
      setMilestones(timelineOverride || TIMELINE);
    }
  }, [timelineOverride, milestones]);

  const headers = { "X-Admin-Passcode": passcode };

  const unlock = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND}/api/admin/verify`, null, { headers });
      setUnlocked(true);
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 429 ? "Too many attempts. Wait a minute and try again." : "Incorrect passcode.");
    }
  };

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy((b) => ({ ...b, [slot]: "upload" }));
    try {
      const body = new FormData();
      body.append("file", file);
      const r = await axios.put(`${BACKEND}/api/admin/images/${slot}`, body, { headers });
      setCurrent((c) => ({ ...c, [slot]: `${BACKEND}${r.data.url}?t=${Date.now()}` }));
      refresh();
      toast.success("Image updated. It is live on the site now.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) toast.error("Session expired — unlock again.");
      else if (status === 413) toast.error("Image is larger than 10 MB.");
      else if (status === 415) toast.error("Only JPEG, PNG or WebP images are accepted.");
      else toast.error("Upload failed. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [slot]: null }));
    }
  };

  const reset = async (slot) => {
    setBusy((b) => ({ ...b, [slot]: "reset" }));
    try {
      await axios.delete(`${BACKEND}/api/admin/images/${slot}`, { headers });
      setCurrent((c) => {
        const next = { ...c };
        delete next[slot];
        return next;
      });
      refresh();
      toast.success("Restored the original placeholder image.");
    } catch {
      toast.error("Could not reset. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [slot]: null }));
    }
  };

  const setMilestone = (i, key, value) =>
    setMilestones((ms) => ms.map((m, idx) => (idx === i ? { ...m, [key]: value } : m)));

  const saveTimeline = async () => {
    if (!milestones?.length) return;
    if (milestones.some((m) => !m.year.trim() || !m.title.trim() || !m.text.trim())) {
      toast.error("Every milestone needs a year, title and text.");
      return;
    }
    setSavingTimeline(true);
    try {
      await axios.put(`${BACKEND}/api/admin/timeline`, { milestones }, { headers });
      refresh();
      toast.success("Timeline saved. It is live on the site now.");
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 401 ? "Session expired — unlock again." : "Could not save. Please try again.");
    } finally {
      setSavingTimeline(false);
    }
  };

  return (
    <div data-testid="admin-images-page" className="min-h-screen bg-espresso text-cream">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <a
          data-testid="admin-back-link"
          href="/"
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-cream/50 hover:text-gold transition-colors"
        >
          <ArrowLeft size={14} /> Back to site
        </a>
        <p className="eyebrow !text-gold mt-10 mb-3">Site Owner</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl">Manage Site</h1>

        {!unlocked ? (
          <form
            data-testid="admin-passcode-form"
            onSubmit={unlock}
            className="mt-12 max-w-sm bg-forest-deep border border-cream/10 rounded-sm p-8"
          >
            <label htmlFor="admin-passcode" className="eyebrow !text-gold block mb-3">
              <Lock size={12} className="inline mr-2 -mt-0.5" aria-hidden="true" />
              Passcode
            </label>
            <input
              id="admin-passcode"
              data-testid="admin-passcode-input"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Enter your passcode"
              className="w-full bg-espresso border border-cream/15 rounded-sm px-4 py-3 text-base sm:text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold transition-colors"
              autoComplete="off"
            />
            <button data-testid="admin-unlock-button" type="submit" className="btn-gold mt-6 w-full justify-center">
              Unlock
            </button>
          </form>
        ) : (
          <>
            <p className="mt-4 text-cream/60 text-sm max-w-xl">
              Upload a new photo for any slot below. Changes appear on the website immediately. JPEG, PNG or
              WebP, up to 10 MB.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {IMAGE_SLOTS.map((slot) => {
                const src = current[slot.key] || DEFAULT_IMAGES[slot.key] || null;
                const isCustom = Boolean(current[slot.key]);
                return (
                  <div
                    key={slot.key}
                    data-testid={`admin-slot-${slot.key}`}
                    className="bg-forest-deep border border-cream/10 rounded-sm overflow-hidden"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-espresso">
                      {src ? (
                        <img
                          data-testid={`admin-preview-${slot.key}`}
                          src={src}
                          alt={slot.label}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          data-testid={`admin-preview-${slot.key}`}
                          className="w-full h-full bg-cream flex flex-col items-center justify-center gap-1"
                        >
                          <span className="font-display text-3xl text-espresso">Masthare Estate</span>
                          <span className="font-mono-alt text-[9px] tracking-[0.3em] uppercase text-amber-brand">
                            Default wordmark
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-baseline justify-between gap-3">
                        <h2 className="font-display text-xl">{slot.label}</h2>
                        {isCustom && (
                          <span className="font-mono-alt text-[9px] tracking-[0.25em] uppercase text-gold">
                            Your photo
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-cream/50">{slot.hint}</p>
                      <div className="mt-4 flex gap-3">
                        <input
                          ref={(el) => (fileInputs.current[slot.key] = el)}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            upload(slot.key, e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                        <button
                          data-testid={`admin-upload-${slot.key}`}
                          onClick={() => fileInputs.current[slot.key]?.click()}
                          disabled={Boolean(busy[slot.key])}
                          className="btn-gold !px-5 !py-2.5 !text-[11px] disabled:opacity-60"
                        >
                          <Upload size={13} aria-hidden="true" />
                          {busy[slot.key] === "upload" ? "Uploading..." : slot.key === "logo" ? "Upload Logo" : "Change Photo"}
                        </button>
                        {isCustom && (
                          <button
                            data-testid={`admin-reset-${slot.key}`}
                            onClick={() => reset(slot.key)}
                            disabled={Boolean(busy[slot.key])}
                            className="btn-outline-cream !px-5 !py-2.5 !text-[11px] disabled:opacity-60"
                          >
                            <RotateCcw size={13} aria-hidden="true" />
                            {busy[slot.key] === "reset" ? "Resetting..." : "Reset"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-20 pt-14 border-t border-cream/10">
              <p className="eyebrow !text-gold mb-3">Heritage Timeline</p>
              <h2 className="font-display font-light text-3xl sm:text-4xl">Edit Milestones</h2>
              <p className="mt-3 text-cream/60 text-sm max-w-xl">
                Update the years, titles and stories shown in the Heritage section. Save to publish.
              </p>

              <div data-testid="timeline-editor" className="mt-8 space-y-5">
                {(milestones || []).map((m, i) => (
                  <div
                    key={i}
                    data-testid={`timeline-edit-${i}`}
                    className="bg-forest-deep border border-cream/10 rounded-sm p-5 sm:p-6"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-4 items-start">
                      <div>
                        <label className="font-mono-alt text-[9px] tracking-[0.25em] uppercase text-amber-brand block mb-2">
                          Year
                        </label>
                        <input
                          data-testid={`timeline-year-${i}`}
                          value={m.year}
                          maxLength={12}
                          onChange={(e) => setMilestone(i, "year", e.target.value)}
                          placeholder="1844"
                          className={fieldCls}
                        />
                      </div>
                      <div>
                        <label className="font-mono-alt text-[9px] tracking-[0.25em] uppercase text-amber-brand block mb-2">
                          Title
                        </label>
                        <input
                          data-testid={`timeline-title-${i}`}
                          value={m.title}
                          maxLength={80}
                          onChange={(e) => setMilestone(i, "title", e.target.value)}
                          placeholder="The Estate is Founded"
                          className={fieldCls}
                        />
                      </div>
                      <button
                        data-testid={`timeline-remove-${i}`}
                        onClick={() => setMilestones((ms) => ms.filter((_, idx) => idx !== i))}
                        disabled={(milestones || []).length <= 1}
                        className="self-end p-2.5 text-cream/40 hover:text-red-300 transition-colors disabled:opacity-30"
                        aria-label={`Remove milestone ${i + 1}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="mt-4">
                      <label className="font-mono-alt text-[9px] tracking-[0.25em] uppercase text-amber-brand block mb-2">
                        Story
                      </label>
                      <textarea
                        data-testid={`timeline-text-${i}`}
                        value={m.text}
                        maxLength={300}
                        rows={2}
                        onChange={(e) => setMilestone(i, "text", e.target.value)}
                        placeholder="A short line about this moment..."
                        className={`${fieldCls} resize-y`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <button
                  data-testid="timeline-add-button"
                  onClick={() => setMilestones((ms) => [...(ms || []), { year: "", title: "", text: "" }])}
                  disabled={(milestones || []).length >= 12}
                  className="btn-outline-cream !text-[11px] disabled:opacity-40"
                >
                  <Plus size={14} aria-hidden="true" /> Add Milestone
                </button>
                <button
                  data-testid="timeline-save-button"
                  onClick={saveTimeline}
                  disabled={savingTimeline}
                  className="btn-gold !text-[11px] disabled:opacity-60"
                >
                  <Save size={14} aria-hidden="true" />
                  {savingTimeline ? "Saving..." : "Save Timeline"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminImages;
